/**
 * Google Drive Bulk Import Utility
 * 
 * This utility helps import videos from Google Drive folder structures
 * into the Firebase database.
 * 
 * Two approaches:
 * 1. Using Google Drive API (requires API key setup)
 * 2. Manual folder structure input (simpler, no API needed)
 */

/**
 * Converts a Google Drive file ID to a shareable preview URL
 * @param {string} fileId - Google Drive file ID
 * @returns {string} - Preview URL
 */
export function getDrivePreviewUrl(fileId) {
  return `https://drive.google.com/file/d/${fileId}/preview`;
}

/**
 * Converts a Google Drive share URL to file ID
 * @param {string} url - Google Drive share URL
 * @returns {string|null} - File ID or null if invalid
 */
export function extractDriveFileId(url) {
  if (!url) return null;
  
  // Handle various Google Drive URL formats
  const patterns = [
    /\/file\/d\/([a-zA-Z0-9_-]+)/,
    /id=([a-zA-Z0-9_-]+)/,
    /\/folders\/([a-zA-Z0-9_-]+)/,
    /\/open\?id=([a-zA-Z0-9_-]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }
  
  return null;
}

/**
 * Parses a folder structure from a JSON input
 * Expected format:
 * {
 *   "folderName": {
 *     "subfolderName": {
 *       "video1.mp4": "https://drive.google.com/file/d/...",
 *       "video2.mp4": "https://drive.google.com/file/d/..."
 *     },
 *     "video3.mp4": "https://drive.google.com/file/d/..."
 *   }
 * }
 * 
 * @param {Object} folderStructure - Nested object representing folder structure
 * @param {string} parentChapter - Parent chapter name (for nested folders)
 * @returns {Array} - Array of video objects with title, url, and chapter
 */
export function parseFolderStructure(folderStructure, parentChapter = "") {
  const videos = [];
  
  function traverse(obj, chapterPath = "") {
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === "string" && (value.includes("drive.google.com") || value.includes("youtube.com") || value.includes("youtu.be"))) {
        // This is a video file
        const videoTitle = key.replace(/\.(mp4|avi|mov|mkv|webm)$/i, "");
        videos.push({
          title: videoTitle,
          url: value,
          chapter: chapterPath || "Uncategorized",
          published: true
        });
      } else if (typeof value === "object" && value !== null) {
        // This is a folder
        const newChapterPath = chapterPath 
          ? `${chapterPath} > ${key}` 
          : key;
        traverse(value, newChapterPath);
      }
    }
  }
  
  traverse(folderStructure, parentChapter);
  return videos;
}

/**
 * Fetches folder structure from Google Drive using API
 * Requires Google Drive API to be set up
 * 
 * @param {string} folderId - Google Drive folder ID
 * @param {string} apiKey - Google Drive API key
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Array>} - Array of video objects
 */
export async function fetchDriveFolderStructure(folderId, apiKey, onProgress = null) {
  if (!folderId || !apiKey) {
    throw new Error("Folder ID and API key are required");
  }

  const videos = [];
  const processedFolders = new Set();
  
  async function fetchFolder(folderId, chapterPath = "") {
    if (processedFolders.has(folderId)) return;
    processedFolders.add(folderId);
    
    try {
      const url = `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+trashed=false&key=${apiKey}&fields=files(id,name,mimeType,webViewLink)`;
      
      let response;
      try {
        response = await fetch(url);
      } catch (fetchError) {
        // Check if it's a CORS error
        if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('CORS')) {
          throw new Error("CORS Error: Google Drive API cannot be accessed directly from the browser due to CORS restrictions. Please use the 'Manual JSON Input' method instead, or set up a backend proxy server.");
        }
        throw fetchError;
      }
      
      const data = await response.json();
      
      // Check for API errors in response
      if (data.error) {
        const errorMsg = data.error.message || data.error.message || 'Unknown API error';
        const errorCode = data.error.code || response.status;
        
        if (errorCode === 403) {
          throw new Error(`API Error 403: ${errorMsg}. The API key may not have permission to access this folder, or the folder is not shared publicly.`);
        } else if (errorCode === 404) {
          throw new Error(`API Error 404: ${errorMsg}. Folder not found. Please check the folder URL.`);
        } else if (errorCode === 400) {
          throw new Error(`API Error 400: ${errorMsg}. Invalid API key or request parameters.`);
        } else {
          throw new Error(`API Error ${errorCode}: ${errorMsg}`);
        }
      }
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
      
      if (!data.files || data.files.length === 0) {
        return;
      }
      
      for (const file of data.files) {
        if (file.mimeType === "application/vnd.google-apps.folder") {
          // It's a folder, recurse
          const newChapterPath = chapterPath 
            ? `${chapterPath} > ${file.name}` 
            : file.name;
          
          if (onProgress) {
            onProgress(`Scanning folder: ${file.name}`);
          }
          
          await fetchFolder(file.id, newChapterPath);
        } else if (file.mimeType && file.mimeType.startsWith("video/")) {
          // It's a video file
          const videoUrl = file.webViewLink || getDrivePreviewUrl(file.id);
          const videoTitle = file.name.replace(/\.(mp4|avi|mov|mkv|webm)$/i, "");
          
          videos.push({
            title: videoTitle,
            url: videoUrl,
            chapter: chapterPath || "Uncategorized",
            published: true
          });
          
          if (onProgress) {
            onProgress(`Found video: ${videoTitle}`);
          }
        }
      }
    } catch (error) {
      console.error(`Error fetching folder ${folderId}:`, error);
      throw error;
    }
  }
  
  await fetchFolder(folderId);
  return videos;
}

/**
 * Parses a simple text format into video structure
 * Supports two formats:
 * 
 * Format 1 - Chapter headers with links:
 * Chapter: Topic 1 Coordinate Geometry
 * https://drive.google.com/file/d/ID1/preview
 * https://drive.google.com/file/d/ID2/preview
 * 
 * Chapter: Topic 3 Geometry
 * https://drive.google.com/file/d/ID3/preview
 * 
 * Format 2 - CSV-like (Chapter, Title, URL):
 * Topic 1, Coordinate Geometry Explanation, https://drive.google.com/file/d/ID1/preview
 * Topic 1, Coordinate Geometry Practice, https://drive.google.com/file/d/ID2/preview
 * 
 * Format 3 - Just URLs (one per line, will use "Uncategorized" as chapter):
 * https://drive.google.com/file/d/ID1/preview
 * https://drive.google.com/file/d/ID2/preview
 * 
 * @param {string} textInput - Text input with video links
 * @returns {Array} - Array of video objects
 */
export function parseSimpleTextFormat(textInput) {
  const videos = [];
  const lines = textInput.split('\n').map(line => line.trim()).filter(line => line);
  
  let currentChapter = "Uncategorized";
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check if it's a chapter header
    if (line.toLowerCase().startsWith('chapter:') || line.toLowerCase().startsWith('chapter ')) {
      currentChapter = line.replace(/^chapter:?\s*/i, '').trim();
      continue;
    }
    
    // Check if it's a CSV-like format (Chapter, Title, URL)
    if (line.includes(',') && line.includes('http')) {
      const parts = line.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        const url = parts.find(p => p.includes('http'));
        const chapter = parts[0] || currentChapter;
        const title = parts.length === 3 ? parts[1] : (parts.find(p => !p.includes('http') && p !== chapter) || extractTitleFromUrl(url));
        
        if (url && isValidVideoUrl(url)) {
          videos.push({
            title: title || extractTitleFromUrl(url),
            url: url,
            chapter: chapter || "Uncategorized",
            published: true
          });
        }
        continue;
      }
    }
    
    // Check if it's a URL
    if (isValidVideoUrl(line)) {
      const title = extractTitleFromUrl(line);
      videos.push({
        title: title,
        url: line,
        chapter: currentChapter,
        published: true
      });
    }
  }
  
  return videos;
}

/**
 * Extracts a title from a URL
 * @param {string} url - Video URL
 * @returns {string} - Extracted title
 */
function extractTitleFromUrl(url) {
  if (!url) return "Untitled Video";
  
  // Try to extract from Google Drive URL
  if (url.includes('drive.google.com')) {
    // Could try to get from URL parameters, but usually not available
    // So we'll use a generic name
    const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (fileIdMatch) {
      return `Video ${fileIdMatch[1].substring(0, 8)}`;
    }
  }
  
  // Try YouTube
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    return "YouTube Video";
  }
  
  return "Video";
}

/**
 * Parses simple text format and extracts video titles from URLs when possible
 * Enhanced version that tries to get titles from the input
 * @param {string} textInput - Text input with video links
 * @returns {Array} - Array of video objects
 */
export function parseSimpleTextFormatWithTitles(textInput) {
  const videos = [];
  const lines = textInput.split('\n').map(line => line.trim()).filter(line => line);
  
  let currentChapter = "Uncategorized";
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check if it's a chapter header
    if (line.toLowerCase().startsWith('chapter:') || line.toLowerCase().startsWith('chapter ')) {
      currentChapter = line.replace(/^chapter:?\s*/i, '').trim();
      continue;
    }
    
    // Check if it's a CSV-like format (Chapter, Title, URL)
    if (line.includes(',') && line.includes('http')) {
      const parts = line.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        const url = parts.find(p => p.includes('http'));
        const chapter = parts[0] || currentChapter;
        const title = parts.length === 3 ? parts[1] : (parts.find(p => !p.includes('http') && p !== chapter) || extractTitleFromUrl(url));
        
        if (url && isValidVideoUrl(url)) {
          videos.push({
            title: title || extractTitleFromUrl(url),
            url: url,
            chapter: chapter || "Uncategorized",
            published: true
          });
        }
        continue;
      }
    }
    
    // Check if it's a URL
    if (isValidVideoUrl(line)) {
      const title = extractTitleFromUrl(line);
      videos.push({
        title: title,
        url: line,
        chapter: currentChapter,
        published: true
      });
    }
  }
  
  return videos;
}

/**
 * Validates video URLs
 * @param {string} url - Video URL to validate
 * @returns {boolean} - True if valid
 */
export function isValidVideoUrl(url) {
  if (!url) return false;
  
  const validPatterns = [
    /drive\.google\.com/,
    /youtube\.com/,
    /youtu\.be/,
    /vimeo\.com/,
    /zoom\.us/
  ];
  
  return validPatterns.some(pattern => pattern.test(url));
}

